import { useCallback, useEffect, useState } from "react";
import clsx from "clsx";
import { CheckCircle2, ClipboardList, Eye, TriangleAlert } from "lucide-react";
import { Button, Modal, Spinner } from "../../../ui/index.ts";
import { Card } from "../../../shared/card/card.tsx";
import { displayFormatDate } from "../../../../../utils/helpers.js";
import { getApi } from "../../../../utils/api.ts";
import { showError } from "../../../../utils/toast.ts";
import { useSqeLabs } from "../../../../hooks/use-sqe-labs.ts";
import { accessToken } from "../../../../utils/auth.ts";
import { API_ASSET_USER, ASSIGN_ASSET } from "../../../../../config/Endpoints.js";
import {
  OffboardingDetail,
  OffboardingSectionType,
} from "../schemas/index.ts";
import type { OffboardingEmployeeDisplay } from "../utils/use-offboarding-employee-display.ts";
import type { OffboardingSource } from "../types.ts";
import {
  ViewResignationFormModal,
  ViewClearanceFormModal,
  WithdrawModal,
  ExitInterviewModal,
} from "../modals/index.ts";
import {
  areAllOtherDepartmentsComplete,
  DEPARTMENT_TO_SECTION,
  shouldPromptExitInterview,
} from "../utils/offboarding-roles.ts";
import { EmployeeHeaderCard } from "./employee-header-card/index.ts";
import {
  ClearanceSummary,
  type ClearanceDepartment,
} from "./clearance-summary/index.ts";
import { ResignationProgressTracker } from "./resignation-progress-tracker/index.ts";
import styles from "./resignation-submitted-card.module.css";

function formatLastWorkingDay(finalLwd: string | null | undefined): string {
  if (finalLwd == null || finalLwd.trim() === "") {
    return "TBD";
  }

  const formatted = displayFormatDate(finalLwd);
  return formatted === "-" ? "TBD" : formatted;
}

type AssetInfo = {
  assetName: string;
  serialNumber: string;
};

type AssignedAssetResponse = {
  data?: Array<{ assestName?: string | null; serialNumber?: string | null }>;
  totalPages?: number;
};

function normalizeValue(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  return value;
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;
const CLEARANCE_ACTION_DAYS_BEFORE_LWD = 7;

function parseUtcDateMs(value: string | null | undefined): number | null {
  if (value == null || value.trim() === "") return null;

  const [datePart] = value.split("T");
  const parts = datePart.split("-").map(Number);
  if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime())
      ? null
      : Date.UTC(
          parsed.getUTCFullYear(),
          parsed.getUTCMonth(),
          parsed.getUTCDate(),
        );
  }

  const [year, month, day] = parts;
  return Date.UTC(year, month - 1, day);
}

function startOfTodayUtcMs(): number {
  const today = new Date();
  return Date.UTC(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );
}

function isSameUtcDate(aMs: number, bMs: number): boolean {
  return aMs === bMs;
}

function daysUntilUtcDate(dateMs: number): number {
  return Math.ceil((dateMs - startOfTodayUtcMs()) / MS_PER_DAY);
}

const DEPARTMENT_FORM_TITLES: Record<ClearanceDepartment, string> = {
  IT: "IT Clearance Form",
  PAYROLL: "Payroll Clearance Form",
  ADMIN: "Admin Clearance Form",
  RM: "Reporting Manager Clearance Form",
  HR: "HR Clearance Form",
};

type ClearanceSummaryConfig = {
  showActions: boolean;
  viewerDepartments?: ClearanceDepartment[];
  canViewAllCompleted?: boolean;
  allOtherDeptsComplete?: boolean;
  exitInterviewAcknowledged?: boolean;
  onTakeAction?: (department: ClearanceDepartment) => void;
};

type ResignationSubmittedCardProps = {
  employee: OffboardingEmployeeDisplay;
  offboardingId: number;
  source?: OffboardingSource;
  detail: OffboardingDetail;
  showWithdraw?: boolean;
  showViewResignationForm?: boolean;
  showViewExitInterviewForm?: boolean;
  showExitInterviewPrompt?: boolean;
  showProgressTracker?: boolean;
  showAllocatedAssets?: boolean;
  assetsForCurrentUser?: boolean;
  clearanceSummary?: ClearanceSummaryConfig;
  onExitInterviewSuccess?: () => void;
} & (
  | { showWithdraw?: true; onWithdrawSuccess: () => void }
  | { showWithdraw: false; onWithdrawSuccess?: () => void }
);

export function ResignationSubmittedCard({
  employee,
  offboardingId,
  source = "REGULAR",
  detail,
  showWithdraw = true,
  showViewResignationForm = true,
  showViewExitInterviewForm = false,
  showExitInterviewPrompt = showWithdraw,
  showProgressTracker = true,
  showAllocatedAssets = false,
  assetsForCurrentUser = false,
  clearanceSummary,
  onWithdrawSuccess = () => {},
  onExitInterviewSuccess = () => {},
}: ResignationSubmittedCardProps) {
  const isSqe = useSqeLabs();
  const [showViewFormModal, setShowViewFormModal] = useState(false);
  const [showViewExitInterviewModal, setShowViewExitInterviewModal] =
    useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [showExitInterviewModal, setShowExitInterviewModal] = useState(false);
  const [showExitInterviewDateNote, setShowExitInterviewDateNote] =
    useState(false);
  const [viewClearanceSection, setViewClearanceSection] = useState<{
    sectionType: OffboardingSectionType;
    title: string;
  } | null>(null);
  const [allocatedAssets, setAllocatedAssets] = useState<AssetInfo[]>([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [assetsError, setAssetsError] = useState<string | null>(null);

  const employeeId = detail.offboarding.employeeId;
  const showAssetColumn = showAllocatedAssets && allocatedAssets.length > 0;

  const fetchAssetInfo = useCallback(async () => {
    setAssetsLoading(true);
    setAssetsError(null);

    try {
      const pageSize = 10;
      const firstUrl = assetsForCurrentUser
        ? `${API_ASSET_USER}?pageSize=${pageSize}&page=1`
        : `${ASSIGN_ASSET}?employeeId=${employeeId}`;
      const { data: firstPage } = await getApi<AssignedAssetResponse>(firstUrl, accessToken());
      const assignedAssets = Array.isArray(firstPage?.data) ? [...firstPage.data] : [];

      if (assetsForCurrentUser) {
        const totalPages = Math.max(1, Number(firstPage?.totalPages) || 1);
        for (let page = 2; page <= totalPages; page += 1) {
          const { data: nextPage } = await getApi<AssignedAssetResponse>(
            `${API_ASSET_USER}?pageSize=${pageSize}&page=${page}`,
            accessToken(),
          );
          if (Array.isArray(nextPage?.data)) assignedAssets.push(...nextPage.data);
        }
      }

      const nextAllocated = assignedAssets.map((item) => ({
        assetName: normalizeValue(item.assestName),
        serialNumber: normalizeValue(item.serialNumber),
      }));

      setAllocatedAssets(nextAllocated);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to load asset information.";
      setAssetsError(message);
      if (isSqe) showError(message);
      setAllocatedAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  }, [assetsForCurrentUser, employeeId, isSqe]);

  useEffect(() => {
    void fetchAssetInfo();
  }, [fetchAssetInfo]);

  const lastWorkingDay = formatLastWorkingDay(detail.offboarding.finalLwd);
  const finalLwdDateMs = parseUtcDateMs(detail.offboarding.finalLwd);
  const todayUtcMs = startOfTodayUtcMs();
  const canFillExitInterview =
    finalLwdDateMs != null && isSameUtcDate(todayUtcMs, finalLwdDateMs);
  const isOnOrAfterLastWorkingDay =
    finalLwdDateMs != null && todayUtcMs >= finalLwdDateMs;
  const clearanceDaysUntilLwd =
    finalLwdDateMs == null ? null : daysUntilUtcDate(finalLwdDateMs);
  // Temporarily allow SQE clearance actions regardless of the LWD window.
  const clearanceActionsAvailable =
    isSqe || (clearanceDaysUntilLwd != null &&
    clearanceDaysUntilLwd <= CLEARANCE_ACTION_DAYS_BEFORE_LWD);
  const clearanceActionUnavailableMessage =
    finalLwdDateMs == null
      ? "Clearance actions will be available once the final last working day is confirmed."
      : `Clearance actions will be available ${CLEARANCE_ACTION_DAYS_BEFORE_LWD} days before the last working day.`;
  const canShowExitInterviewPrompt =
    showExitInterviewPrompt && shouldPromptExitInterview(detail.offboarding);
  const allOtherClearancesComplete = areAllOtherDepartmentsComplete(
    detail.offboarding,
  );
  const canViewExitInterview =
    showViewExitInterviewForm && detail.offboarding.exitInterviewCompleted;
  const exitInterviewAcknowledged = detail.offboarding.isAcknowledged === true;
  const canShowWithdraw = showWithdraw && !isOnOrAfterLastWorkingDay;

  const handleExitInterviewClick = () => {
    if (!canFillExitInterview) {
      setShowExitInterviewDateNote(true);
      return;
    }

    setShowExitInterviewModal(true);
  };

  const handleViewClearanceForm = (department: ClearanceDepartment) => {
    setViewClearanceSection({
      sectionType: DEPARTMENT_TO_SECTION[department],
      title: DEPARTMENT_FORM_TITLES[department],
    });
  };

  return (
    <div className={styles.content}>
      {canShowExitInterviewPrompt && (
        <div className={styles.exitInterviewBanner} role="status">
          <div className={styles.exitInterviewBannerMain}>
            <ClipboardList
              className={styles.exitInterviewBannerIcon}
              size={22}
              strokeWidth={2}
              aria-hidden
            />
            <div className={styles.exitInterviewBannerText}>
              <p className={styles.exitInterviewBannerTitle}>
                Exit Interview Required
              </p>
              <p className={styles.exitInterviewBannerDescription}>
                Your clearances have been approved. Please complete the exit
                interview form on your last working day to finish your
                offboarding process.
              </p>
            </div>
          </div>
          <Button
            variant="accent"
            className={styles.exitInterviewBannerBtn}
            onClick={handleExitInterviewClick}
          >
            Exit Interview Form
          </Button>
        </div>
      )}

      <div
        className={clsx(
          styles.mainSection,
          !canShowWithdraw && styles.mainSectionFull,
        )}
      >
        <EmployeeHeaderCard
          employee={employee}
          lastWorkingDay={lastWorkingDay}
          showViewResignationForm={showViewResignationForm}
          onViewResignationForm={
            showViewResignationForm
              ? () => setShowViewFormModal(true)
              : undefined
          }
        />

        {canShowWithdraw && (
          <Card className={styles.withdrawCard}>
            <h3 className="heading-sm">
              Need to withdraw your Offboarding Request?
            </h3>
            <p className="body-sm">
              You can withdraw your request at any stage before final exit.
            </p>
            <Button
              variant="success"
              onClick={() => setShowWithdrawModal(true)}
            >
              Withdraw Request
            </Button>
          </Card>
        )}
      </div>

      {showProgressTracker && (
        <div className={styles.trackerSection}>
          <ResignationProgressTracker
            status={detail.offboarding.status}
            allOtherClearancesComplete={allOtherClearancesComplete}
          />
        </div>
      )}

      <div className={styles.clearanceSection}>
        {canViewExitInterview && (
          <Card
            className={clsx(
              styles.exitInterviewReviewCard,
              !exitInterviewAcknowledged && styles.exitInterviewReviewCardPending,
            )}
          >
            <div className={styles.exitInterviewReviewMain}>
              <span
                className={clsx(
                  styles.exitInterviewReviewIcon,
                  !exitInterviewAcknowledged &&
                    styles.exitInterviewReviewIconWarning,
                )}
                aria-hidden
              >
                {exitInterviewAcknowledged ? (
                  <ClipboardList size={20} strokeWidth={2} />
                ) : (
                  <TriangleAlert size={20} strokeWidth={2} />
                )}
              </span>
              <div className={styles.exitInterviewReviewText}>
                <h3 className="heading-sm">Exit Interview Form</h3>
                {/* <p className="body-sm">
                  The employee has successfully submitted the exit interview
                  form.
                </p> */}
                {exitInterviewAcknowledged ? (
                  <div className={styles.exitInterviewAcknowledged}>
                    <CheckCircle2 size={14} strokeWidth={2} aria-hidden />
                    <span>Acknowledged</span>
                  </div>
                ) : (
                  <div className={styles.exitInterviewAcknowledgeNotice}>
                    <span>Please acknowledge the exit interview form.</span>
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="accent"
              startIcon={<Eye size={16} strokeWidth={2} aria-hidden />}
              onClick={() => setShowViewExitInterviewModal(true)}
            >
              View Form
            </Button>
          </Card>
        )}

        <div className={clsx(styles.clearanceRow, showAssetColumn && styles.clearanceRowWithAssets)}>
          <ClearanceSummary
            offboarding={detail.offboarding}
            showActions={clearanceSummary?.showActions ?? false}
            viewerDepartments={clearanceSummary?.viewerDepartments}
            canViewAllCompleted={clearanceSummary?.canViewAllCompleted}
            allOtherDeptsComplete={clearanceSummary?.allOtherDeptsComplete}
            exitInterviewAcknowledged={
              clearanceSummary?.exitInterviewAcknowledged
            }
            actionsAvailable={clearanceActionsAvailable}
            actionUnavailableMessage={clearanceActionUnavailableMessage}
            onTakeAction={clearanceSummary?.onTakeAction}
            onViewForm={
              clearanceSummary?.showActions ? handleViewClearanceForm : undefined
            }
          />

          {showAllocatedAssets && allocatedAssets.length > 0 && (
            <Card className={styles.assetsCard}>
              <div className={styles.assetsHeader}>
                <div>
                  <h3 className="heading-sm">Allocated Assets</h3>
                  <p className="body-sm">
                    Assets currently assigned to you.
                  </p>
                </div>
              </div>

              {assetsLoading ? (
                <div className={styles.assetsLoading}>
                  <Spinner />
                </div>
              ) : null}

              {assetsError ? (
                <div className={styles.assetsError} role="alert">
                  <TriangleAlert size={18} aria-hidden />
                  <span className="body-sm">{assetsError}</span>
                </div>
              ) : null}

              <div className={styles.assetListWrapper}>
             

                <div className={styles.assetScrollArea}>
                  {allocatedAssets.map((asset, index) => (
                    <div key={`allocated-${index}`} className={styles.assetRow}>
                      <span className={styles.assetStatusDot} />
                      <div className={styles.assetDetail}>
                        <p
                          className={styles.assetName}
                          title={asset.assetName}
                        >
                          {asset.assetName}
                        </p>
                        <p
                          className={styles.assetSerial}
                          title={asset.serialNumber}
                        >
                          {asset.serialNumber}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Card>
          )}
        </div>
      </div>

      <ViewResignationFormModal
        open={showViewFormModal}
        offboardingId={offboardingId}
        source={source}
        onClose={() => setShowViewFormModal(false)}
      />

      {showViewExitInterviewModal && (
        <ViewClearanceFormModal
          open={showViewExitInterviewModal}
          offboardingId={offboardingId}
          source={source}
          sectionType="EXIT_INTERVIEW"
          title="Exit Interview Form"
          onClose={() => setShowViewExitInterviewModal(false)}
          isAcknowledged={exitInterviewAcknowledged}
          onAcknowledgeSuccess={onExitInterviewSuccess}
        />
      )}

      {viewClearanceSection != null && (
        <ViewClearanceFormModal
          open
          offboardingId={offboardingId}
          source={source}
          sectionType={viewClearanceSection.sectionType}
          title={viewClearanceSection.title}
          onClose={() => setViewClearanceSection(null)}
        />
      )}

      {canShowWithdraw && (
        <WithdrawModal
          open={showWithdrawModal}
          offboardingId={offboardingId}
          onClose={() => setShowWithdrawModal(false)}
          onSuccess={onWithdrawSuccess}
        />
      )}

      {showExitInterviewModal && (
        <ExitInterviewModal
          open={showExitInterviewModal}
          offboardingId={offboardingId}
          onClose={() => setShowExitInterviewModal(false)}
          onSuccess={onExitInterviewSuccess}
        />
      )}

      <Modal
        open={showExitInterviewDateNote}
        onClose={() => setShowExitInterviewDateNote(false)}
        title="Exit Interview Not Available Yet"
        size="sm"
        footer={
          <Button
            variant="accent"
            onClick={() => setShowExitInterviewDateNote(false)}
          >
            Okay
          </Button>
        }
      >
        <div className={styles.exitInterviewDateNote}>
          <span className={styles.exitInterviewDateNoteIcon} aria-hidden>
            <TriangleAlert size={22} strokeWidth={2} />
          </span>
          <div className={styles.exitInterviewDateNoteText}>
            <p className={clsx("body-sm", styles.exitInterviewDateNoteTitle)}>
              Exit Interview Form Available on Last Working Day
            </p>
            <p className="body-sm">
              You can fill the exit interview form only on your last working
              day.
            </p>
          </div>
        </div>
      </Modal>
    </div>
  );
}
